import { Box, Popover, Typography } from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import { useState } from 'react'
import OptionsFilter from '@/components/features/config/columns/Filter/OptionsFilter'
import { useAppTheme } from '@/theme/ThemeContext'

/** Shape every caller passes for `options` — kept local so this primitive doesn't
 * depend on any page-specific option-building module. */
type NodeSelectOption = { id: string; label: string }

const NodeSelect = ({
  placeholder,
  options = [],
  value,
  onChangeValue,
  handleChangeId,
  Icon,
  disabled = false,
  ariaLabel,
}: {
  placeholder: string
  /** Defaults to [] so a caller that omits it can never crash the option lookup below. */
  options?: NodeSelectOption[]
  value?: string | null
  onChangeValue?: (v: string | null) => void
  /** Legacy callback — still invoked alongside onChangeValue for callers that haven't migrated yet. */
  handleChangeId?: (id: number) => void
  Icon?: React.ElementType
  disabled?: boolean
  /** Names the trigger for assistive technology and tests where no visible label sits beside it. */
  ariaLabel?: string
}) => {
  const [open, setOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  // Selected option id as a single-element array (or empty) — derived from controlled `value` prop.
  const selected: string[] =
    value != null && value !== '' ? [String(value)] : []
  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    setOpen(true)
  }

  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const colors = {
    text: isDarkMode ? '#FFFFFF' : '#003F61',
    borderColor: isDarkMode ? '#445876' : 'black',
    disabledBorder: '#C0C2D6',
    iconColor: isDarkMode ? '#FFFFFF' : 'black',
    disabledIcon: '#C0C2D6',
    bg: isDarkMode ? '#0D192F' : 'transparent',
  }

  const handleClose = () => {
    setAnchorEl(null)
    setOpen(false)
  }
  const handleChange = (values: string[]) => {
    // OptionsFilter is a multi-select that APPENDS toggles, so with a
    // pre-selected value the just-picked option is the LAST entry, not the
    // first — values[0] would silently keep the old selection forever.
    const next = values.length > 0 ? values[values.length - 1] : null
    onChangeValue?.(next)
    if (next != null) handleChangeId?.(Number(next))
    // Single-select: close popover so the trigger's selected label is visible.
    // NodeSelectMany keeps its popover open for multi-pick UX.
    handleClose()
  }
  return (
    <>
      {/* nodrag: inside a React Flow node ANY mousedown starts a node drag, so a
       * click that wobbles a pixel never fires. The popover content renders in a
       * portal outside the node, so only this trigger needs the opt-out. */}
      <Box
        className="nodrag"
        role={ariaLabel ? 'button' : undefined}
        aria-label={ariaLabel}
        aria-disabled={ariaLabel && disabled ? true : undefined}
        sx={{
          border: `1px solid ${disabled ? colors.disabledBorder : colors.borderColor}`,
          padding: '5px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 999990,
          cursor: 'pointer',
          width: '130px',
          height: '40px',
          maxHeight: '40px',
          backgroundColor: disabled ? 'transparent' : colors.bg,
        }}
        onClick={disabled ? undefined : handleOpen}
      >
        {open ? (
          <ArrowDropUpIcon
            sx={{ color: disabled ? colors.disabledIcon : colors.iconColor }}
          />
        ) : (
          <ArrowDropDownIcon
            sx={{ color: disabled ? colors.disabledIcon : colors.iconColor }}
          />
        )}
        <Typography
          sx={{
            color: colors.text,
            textAlign: 'right',
            textOverflow: 'ellipsis',
            fontFamily: 'Rubik, sans-serif',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 300,
            lineHeight: '100%',
          }}
        >
          {selected && selected.length > 0
            ? (options ?? []).find((option: NodeSelectOption) =>
                selected.includes(option.id)
              )?.label
            : placeholder}
        </Typography>
      </Box>

      <Popover
        open={open && !disabled}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // nowheel: a long list scrolls, and React Flow would zoom the canvas on the same wheel.
        slotProps={{ paper: { className: 'nowheel' } }}
      >
        <OptionsFilter
          selected={selected}
          checkBox={false}
          options={options}
          onChange={handleChange}
          Icon={Icon}
        />
      </Popover>
    </>
  )
}

export default NodeSelect
